import { Alert } from "@aws-amplify/ui-react";
import { Card, Col, Row, Table } from "react-bootstrap";
import { Lab, RiskScore } from "../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../amplify/api-gateway/search-patients";
import ECGPlot from "../../common/ECGViewer";
import { getUserFriendlyDateStringWithoutSecond } from "../../../utils/time";

/**
 * Modal showing afib risk
 */
export const AfibRisk = (props: {
    patientVisit: PatientVisit,
    riskScores: Record<string, RiskScore>,
    labs: Lab[][],
    tenant: string,
}) => {

    if (!props.riskScores || !props.riskScores["AFIB_PRESENT"]) {
        return <></>
    }

    const formatPreviousCriteriaRiskScore = () => {
        const prevCriteriaScore: RiskScore = props.riskScores["AFIB_NO_PREV_CRITERIA"];

        // # 0 = No previous data
        // # 1 = Previous data, no arrhythmia and no anti-coag
        // # 3 = Previous data, arrhythmia, no anti-coag
        // # 5 = Previous data, no arrhythmia, anti-coag
        // # 7 = Previous data, arrhytnmia, anti-coag
        const score = Number(prevCriteriaScore.prediction);
        if (score === 0) {
            return "No prior visit data";
        } else if (score === 1) {
            return "No prior arrhythmia; No previous anti-coagulants";
        } else if (score === 3) {
            return "Prior arrhythmia; No previous anti-coagulants";
        } else if (score === 5) {
            return "Prior anti-coagulants; No prior arrhythmia";
        } else if (score === 7) {
            return "Prior arrhythmia; Previous anti-coagulants";
        } else {
            return "";
        }
    };

    const ecgLabGroup = props.labs ? props.labs.filter(lab => lab.length > 0 && lab[0].lab_group === "ECG 12-Lead") : [];

    const formatLabs = (labs: Lab[]) => {
        if (labs.length === 0) {
            return (<></>)
        }

        const nameToTimeToLabs: any = {};
        const uniqueAcquisitionTimes = new Set<number>();
        for (let lab of labs) {
            uniqueAcquisitionTimes.add(lab.acquisition_time);
        }
        for (let lab of labs) {
            if (!(lab.lab_name in nameToTimeToLabs)) {
                nameToTimeToLabs[lab.lab_name] = {};
            }
            nameToTimeToLabs[lab.lab_name][lab.acquisition_time] = lab;
        }

        // Don't sort because the labs are standardized order
        const labNames: string[] = [...Object.keys(nameToTimeToLabs)];

        const acquisitionTimes: number[] = Array.from(uniqueAcquisitionTimes).sort((a, b) => a - b);

        const formatLab = (lab: Lab, acquisitionTime: number, labName: string) => {
            if (!lab) {
                // console.warn("Lab is not available for col ", col)
                return <td
                    key={`${acquisitionTime}-${labName}`}
                ></td>
            }

            return (
                <td key={`${acquisitionTime}-${labName}`}>
                    <div>
                        <Row className="list-group-small">
                            <Col md={12}>
                                <span className={(lab.status === "ABNORMAL" || lab.status === "CRITICAL" || lab.status === "ABNORMAL_HIGH" || lab.status === "ABNORMAL_LOW" || lab.status === "CRITICAL_HIGH" || lab.status === "CRITICAL_LOW") ? "lab-group-abnormal" : ""}>
                                    {lab.component_result ? lab.component_result : lab.component_value} {lab.component_unit && lab.component_unit !== "NaN" && lab.component_unit}
                                </span>
                            </Col>
                        </Row>
                    </div>
                </td>
            )
        }

        return (
            <div>
                <Row className="lab-group-table-container">
                    <Col md={12}>
                        <div className="lab-group-header">
                            {labs[0].lab_group}
                        </div>
                        <Table className="lab-group-table">
                            <thead>
                                <tr>
                                    <th>Collection Time</th>
                                    {
                                        // If one column, show all times
                                        acquisitionTimes.map(header => (
                                            <th key={header}>
                                                {getUserFriendlyDateStringWithoutSecond(new Date(header * 1000))}
                                            </th>
                                        ))
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                {labNames.map(labName => (
                                    <tr key={`${labs[0].lab_group}-${labName}`}>
                                        <td key={`${labName}-col`} className="lab-name-col">
                                            {labName}
                                        </td>
                                        {
                                            // If one column, show all times
                                            <>
                                                {acquisitionTimes.map(acquisitionTime => {
                                                    const lab: Lab = nameToTimeToLabs[labName][acquisitionTime];
                                                    return formatLab(lab, acquisitionTime, labName);
                                                })}
                                            </>
                                        }
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </div>
        )
    }

    return (
        <div className="mt-2">
            {
                <Col>
                    <Row xs={1} md={1} className="g-2 mb-2">
                        <Col>
                            <Card>
                                <Card.Body>
                                    <div>
                                        <Alert variation="info">
                                            <strong>Atrial Fibrillation Detected</strong> &bull; {formatPreviousCriteriaRiskScore()}{' '} &bull; CHA<sub>2</sub>DS<sub>2</sub>–VASc: {props.riskScores["CHA2DS2_VASC"].prediction}
                                        </Alert>
                                    </div>
                                    <div>
                                        <ECGPlot internalCSN={props.patientVisit.internal_csn} internalMRN={props.patientVisit.internal_mrn} tenant={props.tenant} afibRisk={props.riskScores["AFIB_PRESENT"]} />
                                    </div>
                                    {
                                        ecgLabGroup.length > 0 && (
                                            <>{formatLabs(ecgLabGroup[0])}</>
                                        )
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            }
        </div>
    );
};
